import React from 'react';
import { Sparkles, Brain, Link as LinkIcon, FileText, Image as ImageIcon, Play, MessageCircle } from 'lucide-react';

/**
 * Play Store Feature Graphic - fixed 1024x500 canvas, no scroll.
 *
 * Capture instructions:
 *   1. Open /promo/feature-graphic in a browser
 *   2. Set viewport to exactly 1024x500
 *   3. Screenshot the full viewport (NOT full page)
 *
 * Or via Playwright:
 *   page.set_viewport_size({"width": 1024, "height": 500})
 *   await page.screenshot(path="feature-graphic.png", full_page=False)
 *
 * Design language matches PlayScreenshots: cream bg + dark olive brand
 * + mint emerald accent, plus a hint of UI in the background so the
 * graphic feels like the product, not a marketing slogan.
 */
const FRAME_W = 1024;
const FRAME_H = 500;

export default function PlayFeatureGraphic() {
    return (
        <div
            className="relative overflow-hidden"
            style={{
                width: FRAME_W,
                height: FRAME_H,
                background: 'radial-gradient(120% 80% at 80% 50%, #DCFCE7 0%, #FDFCF8 55%)',
            }}
        >
            {/* Floating UI artefacts - purposely cropped, slightly out
                of focus so they suggest "the product" without competing
                for attention with the wordmark. */}
            <FloatingCard
                top={60}
                left={510}
                rotate={-7}
                width={280}
                icon={Play}
                label="REEL"
                title="The neuroscience of focus"
                tags={['AI summary', '1m read']}
            />
            <FloatingCard
                top={250}
                left={720}
                rotate={5}
                width={260}
                icon={FileText}
                label="ARTICLE"
                title="Building your second brain"
                tags={['Saved 2m ago']}
            />
            <FloatingChat top={185} left={620} />

            {/* Brand block - left-anchored so the right side gets the
                product collage. Generous left padding so the text never
                feels cramped against the canvas edge. */}
            <div className="absolute inset-y-0 left-0 flex flex-col justify-center px-14" style={{ width: 540 }}>
                {/* Logo + Wordmark row */}
                <div className="flex items-center gap-4 mb-7">
                    <div className="w-[72px] h-[72px] rounded-2xl overflow-hidden shadow-[0_14px_30px_-10px_rgba(46,58,47,0.55)]">
                        <img src="/icon-192.png" alt="BrainSync" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <h1
                            className="font-heading font-black leading-none tracking-tight text-[#1A2F1B]"
                            style={{ fontSize: 52 }}
                        >
                            Brain<span className="text-emerald-500">Sync</span>
                        </h1>
                        <p
                            className="font-mono uppercase tracking-[0.30em] text-emerald-700 mt-1.5"
                            style={{ fontSize: 12 }}
                        >
                            Smart Bookmarks · AI Powered
                        </p>
                    </div>
                </div>

                {/* Headline + supporting line - kept punchy because the
                    feature graphic is glanced at, not read. The colored
                    "Remember" word anchors the eye on the payoff verb
                    (you remember = the product's job is done). */}
                <h2
                    className="font-heading font-black leading-[1.05] text-[#1A2F1B] whitespace-nowrap"
                    style={{ fontSize: 38 }}
                >
                    Save anything.<br />
                    <span className="text-emerald-500">Remember</span> everything.
                </h2>
                <p
                    className="mt-4 text-[#3F4D40] leading-snug"
                    style={{ fontSize: 18, maxWidth: 420 }}
                >
                    <Sparkles className="inline w-4 h-4 text-emerald-600 -mt-1 mr-1" />
                    AI transcribes, summarizes &amp; organizes every link, video, and reel you save.
                </p>
            </div>
        </div>
    );
}

/* ── Decorative card ─────────────────────────────────────────────── */
const FloatingCard = ({ top, left, rotate, width, icon: Icon, label, title, tags = [] }) => (
    <div
        className="absolute bg-white rounded-2xl shadow-[0_20px_40px_-15px_rgba(46,58,47,0.35)] border border-emerald-100/80"
        style={{ top, left, width, transform: `rotate(${rotate}deg)` }}
    >
        <div className="px-4 py-3 flex items-center gap-2 border-b border-emerald-50">
            <div className="w-7 h-7 rounded-md bg-emerald-100 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-emerald-700" />
            </div>
            <span className="font-mono uppercase tracking-[0.2em] text-emerald-700" style={{ fontSize: 10 }}>
                {label}
            </span>
        </div>
        <div className="px-4 py-3">
            <p className="font-semibold leading-tight text-[#1A2F1B]" style={{ fontSize: 14 }}>
                {title}
            </p>
            {tags.length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                    {tags.map((t, i) => (
                        <span
                            key={i}
                            className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium"
                            style={{ fontSize: 9 }}
                        >
                            {t}
                        </span>
                    ))}
                </div>
            )}
        </div>
    </div>
);

const FloatingChat = ({ top, left }) => (
    <div
        className="absolute flex items-center gap-2 bg-[#1A2F1B] text-white rounded-full px-3 py-2 shadow-[0_18px_30px_-10px_rgba(0,0,0,0.4)]"
        style={{ top, left, transform: 'rotate(-2deg)' }}
    >
        <MessageCircle className="w-4 h-4 text-emerald-300" />
        <span style={{ fontSize: 12 }} className="font-medium pr-1">Ask BrainSync…</span>
    </div>
);
